import { Device, Project, RuuviTag } from '../types'
import { AlertGroup, AlertTemplate, AlertVariables } from '../types/Alerts'
import { AlertRuleDataGridRow } from './AlertRuleDataGrid'
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogProps,
  DialogTitle,
  Grid,
  List,
  ListItem,
  ListItemText,
  TextField,
  TextFieldProps,
  Typography,
  TypographyProps
} from '@mui/material'
import { Trans, useTranslation } from 'react-i18next'
import { useState } from 'react'
import { LoadingButton } from '@mui/lab'
import { translateAlertRuleName, translateAlertSeverity } from '../utils/alert'
import { useDispatch } from 'react-redux'
import { createErrorNotification, createSuccessNotification } from '../redux/slices/notifications'
import AlertRuleFunctionSelect, { AlertRuleFunction } from './AlertRuleFunctionSelect'
import { AlertGroupName } from '../enums'
import { useAlertRules } from '../hooks'

export interface AlertRuleEditDialogProps extends Omit<DialogProps, 'children'> {
  alertRules: AlertGroup[]
  alertTemplates: AlertTemplate[]
  rows: AlertRuleDataGridRow[]
  row: AlertRuleDataGridRow
  project?: Project
  device?: Device
  ruuviTag?: RuuviTag
  useRedux?: boolean
  beforeSubmit?: (alertRules: AlertGroup[]) => void
  afterSubmit?: (alertRules: AlertGroup[]) => void
  onClose: () => void
}
export default function AlertRuleEditDialog({
  alertRules,
  alertTemplates,
  rows,
  row,
  project,
  device,
  ruuviTag,
  useRedux = true,
  beforeSubmit,
  afterSubmit,
  open,
  maxWidth = 'md',
  fullWidth = true,
  onClose,
  ...props
}: AlertRuleEditDialogProps) {
  /**
   * The translate function.
   */
  const { t } = useTranslation('common')

  /**
   * The dispatch function.
   */
  const dispatch = useDispatch()

  /**
   * The alert rules state.
   */
  const { updateAlertRules } = useAlertRules({
    project,
    device,
    ruuviTag,
    alertTemplates,
    alertRules,
    useRedux
  })

  /**
   * The function.
   */
  const [alertRuleFunction, setAlertRuleFunction] = useState<AlertRuleFunction>('avg_over_time')

  /**
   * The variables.
   */
  const [variables, setVariables] = useState<AlertVariables>(row.variables)

  /**
   * Indicates if the alert rule is being saved.
   */
  const [isSubmitting, setSubmitting] = useState<boolean>(false)

  /**
   * Handle the submit.
   */
  async function handleSubmit() {
    try {
      const input = rows.map((r) => (r.id === row.id ? { ...row, variables } : r))

      setSubmitting(true)
      beforeSubmit?.(alertRules)

      const updatedAlertRules = await updateAlertRules(input)

      setSubmitting(false)
      dispatch(createSuccessNotification(t('updateAlertRulesSuccess')))
      afterSubmit?.(updatedAlertRules)
    } catch (_error) {
      setSubmitting(false)
      dispatch(createErrorNotification(t('updateAlertRulesError')))
    }
  }

  return (
    <Dialog open={open} maxWidth={maxWidth} fullWidth={fullWidth} {...props} onClose={onClose}>
      <DialogTitle>{t('editAlertRule')}</DialogTitle>
      <DialogContent>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <List>
              <ListItem key="name">
                <ListItemText secondary={t('name')} primary={translateAlertRuleName(row.name)} />
              </ListItem>

              {row?.severity && (
                <ListItem key="severity">
                  <ListItemText
                    secondary={t('severity')}
                    primary={translateAlertSeverity(row.severity)}
                  />
                </ListItem>
              )}

              {Object.keys(row.variables).some(
                (key) => row.variables[key as keyof AlertVariables]?.unit === 'function'
              ) && (
                <ListItem key="function">
                  <ListItemText
                    secondary={t('function')}
                    primary={
                      <AlertRuleFunctionSelect
                        value={alertRuleFunction}
                        onChange={(_, value) => setAlertRuleFunction(value)}
                      />
                    }
                  />
                </ListItem>
              )}

              <ListItem key="rule">
                <ListItemText
                  secondary={t('rule')}
                  primary={
                    <AlertRuleExpressionForm
                      row={row}
                      variables={variables}
                      onChange={(value) => setVariables(value)}
                    />
                  }
                />
              </ListItem>
            </List>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button variant="text" color="inherit" onClick={onClose}>
          {t('cancel')}
        </Button>
        <LoadingButton
          variant="contained"
          color="primary"
          loading={isSubmitting}
          onClick={handleSubmit}
        >
          {t('save')}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  )
}

interface AlertRuleExpressionFormProps {
  row: AlertRuleDataGridRow
  variables: AlertVariables
  onChange?: (value: AlertVariables) => void
}

function AlertRuleExpressionForm({ row, variables, onChange }: AlertRuleExpressionFormProps) {
  switch (row.group) {
    case AlertGroupName.CURRENT:
      return <CurrentExpressionForm row={row} variables={variables} onChange={onChange} />

    case AlertGroupName.DEVICE:
      return <DeviceExpressionForm row={row} variables={variables} onChange={onChange} />

    case AlertGroupName.SIGNAL:
      return <SignalExpressionForm row={row} variables={variables} onChange={onChange} />

    case AlertGroupName.VOLTAGE:
      return <VoltageExpressionForm row={row} variables={variables} onChange={onChange} />

    case AlertGroupName.BATTERY:
      return <BatteryExpressionForm row={row} variables={variables} onChange={onChange} />

    case AlertGroupName.DEW_POINT:
      return <DewPointExpressionForm row={row} variables={variables} onChange={onChange} />

    case AlertGroupName.HUMIDITY:
      return <HumidityExpressionForm row={row} variables={variables} onChange={onChange} />

    case AlertGroupName.PRESSURE:
      return <PressureExpressionForm row={row} variables={variables} onChange={onChange} />

    case AlertGroupName.TEMPERATURE:
      return <TemperatureExpressionForm row={row} variables={variables} onChange={onChange} />

    case AlertGroupName.CONDENSATION_RISK:
      return <CondensationRiskExpressionForm row={row} variables={variables} onChange={onChange} />

    case AlertGroupName.DEVICE_OFFLINE:
      return <DeviceOfflineExpressionForm row={row} variables={variables} onChange={onChange} />

    case AlertGroupName.EXHAUST_AIR_TOO_COLD:
      return <ExhaustAirTooColdExpressionForm row={row} variables={variables} onChange={onChange} />

    case AlertGroupName.EXHAUST_AIR_TOO_HOT:
      return <ExhaustAirTooHotExpressionForm row={row} variables={variables} onChange={onChange} />

    case AlertGroupName.HEATING_WHILE_VALVE_CLOSED:
      return (
        <HeatingWhileValveClosedExpressionForm
          row={row}
          variables={variables}
          onChange={onChange}
        />
      )

    case AlertGroupName.LOW_BATTERY:
      return <LowBatteryExpressionForm row={row} variables={variables} onChange={onChange} />

    case AlertGroupName.NO_HEATING:
      return <NoHeatingExpressionForm row={row} variables={variables} onChange={onChange} />

    case AlertGroupName.TARGET_TEMPERATURE_NOT_REACHED:
      return (
        <TargetTemperatureNotReachedExpressionForm
          row={row}
          variables={variables}
          onChange={onChange}
        />
      )

    case AlertGroupName.TEMPERATURE_RAPID_CHANGE:
      return (
        <TemperatareRapidChangeExpressionForm row={row} variables={variables} onChange={onChange} />
      )

    case AlertGroupName.WEAK_SIGNAL:
      return <WeakSignalExpressionForm row={row} variables={variables} onChange={onChange} />

    default:
      return null
  }
}

function CurrentExpressionForm({ row, variables, onChange }: AlertRuleExpressionFormProps) {
  return (
    <AlertRuleExpression>
      <Trans
        ns="alert"
        i18nKey={`AlertRule.${row.name}.expression`}
        components={{
          param1: (
            <PercentageField
              value={variables.param1!.value as number}
              autoFocus
              onChange={(value) => {
                onChange?.({
                  ...variables,
                  param1: {
                    ...variables.param1,
                    value
                  }
                })
              }}
            />
          )
        }}
      />
    </AlertRuleExpression>
  )
}

function VoltageExpressionForm({ row, variables, onChange }: AlertRuleExpressionFormProps) {
  return (
    <AlertRuleExpression>
      <Trans
        ns="alert"
        i18nKey={`AlertRule.${row.name}.expression`}
        values={{ unit: 'V' }}
        components={{
          param1: (
            <VoltField
              value={variables.param1!.value as number}
              autoFocus
              onChange={(value) => {
                if (value >= 0) {
                  onChange?.({
                    ...variables,
                    param1: {
                      ...variables.param1,
                      value
                    }
                  })
                }
              }}
            />
          )
        }}
      />
    </AlertRuleExpression>
  )
}

function DeviceExpressionForm({ row }: AlertRuleExpressionFormProps) {
  return (
    <AlertRuleExpression>
      <Trans
        ns="alert"
        i18nKey={`AlertRule.${row.name}.expression`}
        components={{
          param1: '3'
        }}
      />
    </AlertRuleExpression>
  )
}

function DewPointExpressionForm({ row, variables, onChange }: AlertRuleExpressionFormProps) {
  return (
    <AlertRuleExpression>
      <Trans
        ns="alert"
        i18nKey={`AlertRule.${row.name}.expression`}
        components={{
          param1: (
            <CelsiusField
              value={variables.param1!.value as number}
              autoFocus
              onChange={(value) => {
                onChange?.({
                  ...variables,
                  param1: {
                    ...variables.param1,
                    value
                  }
                })
              }}
            />
          )
        }}
      />
    </AlertRuleExpression>
  )
}

function HumidityExpressionForm({ row, variables, onChange }: AlertRuleExpressionFormProps) {
  return (
    <AlertRuleExpression>
      <Trans
        ns="alert"
        i18nKey={`AlertRule.${row.name}.expression`}
        components={{
          param1: (
            <PercentageField
              value={variables.param1!.value as number}
              autoFocus
              onChange={(value) => {
                onChange?.({
                  ...variables,
                  param1: {
                    ...variables.param1,
                    value
                  }
                })
              }}
            />
          )
        }}
      />
    </AlertRuleExpression>
  )
}

function PressureExpressionForm({ row, variables, onChange }: AlertRuleExpressionFormProps) {
  return (
    <AlertRuleExpression>
      <Trans
        ns="alert"
        i18nKey={`AlertRule.${row.name}.expression`}
        components={{
          param1: (
            <HectopascalField
              value={variables.param1!.value as number}
              autoFocus
              onChange={(value) => {
                onChange?.({
                  ...variables,
                  param1: {
                    ...variables.param1,
                    value
                  }
                })
              }}
            />
          )
        }}
      />
    </AlertRuleExpression>
  )
}

function TemperatureExpressionForm({ row, variables, onChange }: AlertRuleExpressionFormProps) {
  return (
    <AlertRuleExpression>
      <Trans
        ns="alert"
        i18nKey={`AlertRule.${row.name}.expression`}
        components={{
          param1: (
            <CelsiusField
              value={variables.param1!.value as number}
              autoFocus
              onChange={(value) => {
                onChange?.({
                  ...variables,
                  param1: {
                    ...variables.param1,
                    value
                  }
                })
              }}
            />
          )
        }}
      />
    </AlertRuleExpression>
  )
}

function SignalExpressionForm({ row, variables, onChange }: AlertRuleExpressionFormProps) {
  return (
    <AlertRuleExpression>
      <Trans
        ns="alert"
        i18nKey={`AlertRule.${row.name}.expression`}
        components={{
          param1: (
            <DecibelMilliwattField
              value={variables.param1!.value as number}
              autoFocus
              onChange={(value) => {
                onChange?.({
                  ...variables,
                  param1: {
                    ...variables.param1,
                    value
                  }
                })
              }}
            />
          )
        }}
      />
    </AlertRuleExpression>
  )
}

function BatteryExpressionForm({ row, variables, onChange }: AlertRuleExpressionFormProps) {
  return (
    <AlertRuleExpression>
      <Trans
        ns="alert"
        i18nKey={`AlertRule.${row.name}.expression`}
        values={{ unit: 'V' }}
        components={{
          param1: (
            <VoltField
              value={variables.param1!.value as number}
              autoFocus
              onChange={(value) => {
                if (value >= 0) {
                  onChange?.({
                    ...variables,
                    param1: {
                      ...variables.param1,
                      value
                    }
                  })
                }
              }}
            />
          )
        }}
      />
    </AlertRuleExpression>
  )
}

function CondensationRiskExpressionForm({
  row,
  variables,
  onChange
}: AlertRuleExpressionFormProps) {
  return (
    <AlertRuleExpression>
      <Trans
        ns="alert"
        i18nKey={`AlertRule.${row.name}.expression`}
        components={{
          param1: (
            <CelsiusField
              value={variables.param1!.value as number}
              autoFocus
              onChange={(value) => {
                onChange?.({
                  ...variables,
                  param1: {
                    ...variables.param1,
                    value
                  }
                })
              }}
            />
          )
        }}
      />
    </AlertRuleExpression>
  )
}

function DeviceOfflineExpressionForm({ row, variables, onChange }: AlertRuleExpressionFormProps) {
  return (
    <Typography sx={{ display: 'inline-flex', alignItems: 'center', flexWrap: 'wrap' }}>
      <Trans
        ns="alert"
        i18nKey={`AlertRule.${row.name}.expression`}
        components={{
          param1: (
            <MinutesField
              value={variables.param1!.value as number}
              autoFocus
              onChange={(value) => {
                onChange?.({
                  ...variables,
                  param1: {
                    ...variables.param1,
                    value: value
                  }
                })
              }}
            />
          )
        }}
      />
    </Typography>
  )
}

function ExhaustAirTooColdExpressionForm({
  row,
  variables,
  onChange
}: AlertRuleExpressionFormProps) {
  return (
    <AlertRuleExpression>
      <Trans
        ns="alert"
        i18nKey={`AlertRule.${row.name}.expression`}
        components={{
          param1: (
            <CelsiusField
              value={variables.param1!.value as number}
              onChange={(value) => {
                onChange?.({
                  ...variables,
                  param1: {
                    ...variables.param1,
                    value
                  }
                })
              }}
            />
          )
        }}
      />
    </AlertRuleExpression>
  )
}

function ExhaustAirTooHotExpressionForm({
  row,
  variables,
  onChange
}: AlertRuleExpressionFormProps) {
  return (
    <AlertRuleExpression>
      <Trans
        ns="alert"
        i18nKey={`AlertRule.${row.name}.expression`}
        components={{
          param1: (
            <CelsiusField
              value={variables.param1!.value as number}
              onChange={(value) => {
                onChange?.({
                  ...variables,
                  param1: {
                    ...variables.param1,
                    value
                  }
                })
              }}
            />
          )
        }}
      />
    </AlertRuleExpression>
  )
}

function HeatingWhileValveClosedExpressionForm({
  row,
  variables,
  onChange
}: AlertRuleExpressionFormProps) {
  return (
    <AlertRuleExpression>
      <Trans
        ns="alert"
        i18nKey={`AlertRule.${row.name}.expression`}
        components={{
          param1: (
            <CelsiusField
              value={variables.param1!.value as number}
              autoFocus
              onChange={(value) => {
                if (value >= 0 && value <= 99) {
                  onChange?.({
                    ...variables,
                    param1: {
                      ...variables.param1,
                      value
                    }
                  })
                }
              }}
            />
          ),
          param2: (
            <PercentageField
              value={variables.param2!.value as number}
              onChange={(value) => {
                onChange?.({
                  ...variables,
                  param2: {
                    ...variables.param2!,
                    value
                  }
                })
              }}
            />
          )
        }}
      />
    </AlertRuleExpression>
  )
}

function LowBatteryExpressionForm({ row, variables, onChange }: AlertRuleExpressionFormProps) {
  return (
    <AlertRuleExpression>
      <Trans
        ns="alert"
        i18nKey={`AlertRule.${row.name}.expression`}
        values={{ unit: 'mV' }}
        components={{
          param1: (
            <MillivoltField
              value={variables.param1!.value as number}
              autoFocus
              onChange={(value) => {
                if (value >= 0) {
                  onChange?.({
                    ...variables,
                    param1: {
                      ...variables.param1,
                      value
                    }
                  })
                }
              }}
            />
          )
        }}
      />
    </AlertRuleExpression>
  )
}

function NoHeatingExpressionForm({ row, variables, onChange }: AlertRuleExpressionFormProps) {
  return (
    <AlertRuleExpression>
      <Trans
        ns="alert"
        i18nKey={`AlertRule.${row.name}.expression`}
        components={{
          param1: (
            <CelsiusField
              value={variables.param1!.value as number}
              autoFocus
              onChange={(value) => {
                if (value >= 0 && value <= 99) {
                  onChange?.({
                    ...variables,
                    param1: {
                      ...variables.param1,
                      value
                    }
                  })
                }
              }}
            />
          ),
          param2: (
            <PercentageField
              value={variables.param2!.value as number}
              onChange={(value) => {
                onChange?.({
                  ...variables,
                  param2: {
                    ...variables.param2!,
                    value
                  }
                })
              }}
            />
          )
        }}
      />
    </AlertRuleExpression>
  )
}

function TargetTemperatureNotReachedExpressionForm({
  row,
  variables,
  onChange
}: AlertRuleExpressionFormProps) {
  return (
    <AlertRuleExpression>
      <Trans
        ns="alert"
        i18nKey={`AlertRule.${row.name}.expression`}
        components={{
          param1: (
            <CelsiusField
              value={variables.param1!.value as number}
              autoFocus
              onChange={(value) => {
                onChange?.({
                  ...variables,
                  param1: {
                    ...variables.param1,
                    value
                  }
                })
              }}
            />
          ),
          param2: (
            <MinutesField
              value={variables.param2!.value as number}
              onChange={(value) => {
                onChange?.({
                  ...variables,
                  param2: {
                    ...variables.param2!,
                    value: value
                  }
                })
              }}
            />
          )
        }}
      />
    </AlertRuleExpression>
  )
}

function TemperatareRapidChangeExpressionForm({
  row,
  variables,
  onChange
}: AlertRuleExpressionFormProps) {
  return (
    <AlertRuleExpression>
      <Trans
        ns="alert"
        i18nKey={`AlertRule.${row.name}.expression`}
        components={{
          param1: (
            <CelsiusField
              value={variables.param1!.value as number}
              autoFocus
              onChange={(value) => {
                onChange?.({
                  ...variables,
                  param1: {
                    ...variables.param1,
                    value
                  }
                })
              }}
            />
          )
        }}
      />
    </AlertRuleExpression>
  )
}

function WeakSignalExpressionForm({ row, variables, onChange }: AlertRuleExpressionFormProps) {
  return (
    <AlertRuleExpression>
      <Trans
        ns="alert"
        i18nKey={`AlertRule.${row.name}.expression`}
        components={{
          param1: (
            <DecibelMilliwattField
              value={variables.param1!.value as number}
              autoFocus
              onChange={(value) => {
                onChange?.({
                  ...variables,
                  param1: {
                    ...variables.param1,
                    value
                  }
                })
              }}
            />
          )
        }}
      />
    </AlertRuleExpression>
  )
}

function AlertRuleExpression({ children, sx, ...props }: TypographyProps) {
  return (
    <Typography
      sx={{ display: 'inline-flex', alignItems: 'flex-start', flexWrap: 'wrap', ...sx }}
      {...props}
    >
      {children}
    </Typography>
  )
}

interface NumberFieldProps extends Omit<TextFieldProps, 'value' | 'onChange'> {
  value: number
  onChange?: (value: number) => void
}

function NumberField({ value, onChange, sx, ...props }: NumberFieldProps) {
  return (
    <TextField
      value={value}
      size="small"
      inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
      variant="standard"
      sx={{ width: `${Math.max(value.toString().length, 1)}ch`, mx: 1, ...sx }}
      {...props}
      onChange={(event) => {
        const value = Number(event.target.value)

        if (!isNaN(value)) {
          onChange?.(value)
        }
      }}
    />
  )
}

function CelsiusField({ value, onChange, ...props }: NumberFieldProps) {
  return <NumberField {...props} value={value} onChange={onChange} />
}

function HectopascalField({ value, onChange, ...props }: NumberFieldProps) {
  return (
    <NumberField
      {...props}
      value={value}
      onChange={(value) => {
        if (value >= 0) {
          onChange?.(value)
        }
      }}
    />
  )
}

function PercentageField({ value, onChange, ...props }: NumberFieldProps) {
  return (
    <NumberField
      {...props}
      value={value}
      onChange={(value) => {
        if (value >= 0 && value < 100) {
          onChange?.(value)
        }
      }}
    />
  )
}

function DecibelMilliwattField({ value, onChange, ...props }: NumberFieldProps) {
  return <NumberField {...props} value={value} onChange={onChange} />
}

function VoltField({ value, onChange, ...props }: NumberFieldProps) {
  return (
    <NumberField
      {...props}
      value={value}
      onChange={(value) => {
        if (value >= 0) {
          onChange?.(value)
        }
      }}
    />
  )
}

function MillivoltField({ value, onChange, ...props }: NumberFieldProps) {
  return (
    <NumberField
      {...props}
      value={value}
      onChange={(value) => {
        if (value >= 0) {
          onChange?.(value)
        }
      }}
    />
  )
}

function MinutesField({ value, onChange, ...props }: NumberFieldProps) {
  const ONE_DAY = 24 * 60

  return (
    <NumberField
      {...props}
      value={value}
      onChange={(value) => {
        if (value >= 0 && value <= ONE_DAY) {
          onChange?.(value)
        }
      }}
    />
  )
}
